'use client';

/*
Assurance — mise en page alignée sur la homepage v5.
*/
import ORImage from 'next/image';
import Head from '@/core/head';
import { Markdown, lang, hocT } from '@/helpers/translate';
import { useZusHomeStore } from '@/helpers/context';
import { getCanonicalUrl } from '@/helpers/router.js';
import { getImageOutside } from '@/helpers/common';
import { FadeUp, Stagger, StaggerChild } from '@/core/motion';
import {
  SECTION, CARD, H2, Kicker, ArrowIcon, NumberedList, kicker,
} from '@/core/editorial';

const HELP_LINKS = [
  ['1', 'https://help.wearescene.com/fr/articles/8027868-qu-est-ce-qu-un-sinistre'],
  ['2', 'https://help.wearescene.com/fr/articles/8027874-quand-declarer-mon-sinistre'],
  ['4', 'https://help.wearescene.com/fr/articles/8027899-comment-completer-ma-declaration-de-sinistre'],
  ['5', 'https://help.wearescene.com/fr/articles/8027895-combien-de-temps-avant-de-toucher-le-dedommagement-lie-a-un-sinistre'],
  ['6', 'https://help.wearescene.com/fr/articles/8027862-principales-exclusions-de-la-garantie-hotes-la-particularite-des-objets-de-valeur-et-de-l-electronique-portable-pourquoi-sont-ils-exclus'],
  ['7', 'https://help.wearescene.com/fr/articles/8027863-que-faire-avec-mes-objets-de-valeur'],
];

const MD = 'text-[15px] leading-relaxed text-was-slate [&_strong]:font-medium [&_strong]:text-was-ink [&_ul]:mt-3 [&_ul]:list-none [&_ul]:space-y-2 [&_ul]:p-0 [&_li]:relative [&_li]:pl-5 [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:top-[0.65em] [&_li]:before:size-1.5 [&_li]:before:rounded-full [&_li]:before:bg-was-ink [&_p+p]:mt-3';

export default hocT(({ t }) => {
  const { ua, extWebpJpg: extension } = useZusHomeStore.getState();
  const langCurrent = lang();
  const canurls = getCanonicalUrl('insurance', langCurrent);

  const steps = [0, 1, 2].map((i) => ({ title: t(`insurance:steps.${i}.title`), body: t(`insurance:steps.${i}.text`) }));
  const faq = [1, 2, 3, 4, 5, 6].map((i) => ({ title: t(`insurance:faq.${i}.question`), body: t(`insurance:faq.${i}.text`) }));

  return (
    <div className="bg-white font-display text-was-ink">
      <Head>
        {canurls.map((cprops) => <link key={JSON.stringify(cprops)} {...cprops} />)}
      </Head>

      {/* Hero */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1240px] px-6 pb-16 pt-28 lg:px-8 lg:pb-24 lg:pt-36">
          <FadeUp>
            <div className="flex flex-wrap items-center justify-between gap-6">
              <Kicker n="01">{kicker(langCurrent, 'insurance', 0)}</Kicker>
              <div className="flex items-center gap-3 text-[13px] text-was-muted">
                <span>{t('core:axa')}</span>
                <img src="/img/axa-logo.svg" alt="AXA" className="static size-9 rounded-[6px]" loading="lazy" />
              </div>
            </div>
            <h1 className="mt-5 max-w-[900px] text-balance font-heading text-[32px] font-extrabold uppercase leading-[1.04] tracking-[-0.01em] md:text-[40px] lg:text-[46px]">{t('insurance:title')}</h1>
          </FadeUp>
          <div className="mt-10 grid gap-8 border-t border-was-ink/8 pt-10 md:grid-cols-2 lg:mt-12 lg:gap-16">
            <FadeUp delay={0.05}>
              <Markdown className="text-lg leading-relaxed text-was-ink [&_strong]:font-medium">{t('insurance:intro_1')}</Markdown>
            </FadeUp>
            <FadeUp delay={0.1}>
              <Markdown className="text-[15px] leading-relaxed text-was-slate [&_strong]:font-medium [&_strong]:text-was-ink">{t('insurance:intro_2')}</Markdown>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="border-t border-was-ink/8 bg-was-cream">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="02">{kicker(langCurrent, 'insurance', 1)}</Kicker>
          </FadeUp>
          <div className="mt-10">
            <NumberedList items={steps} columns={3} />
          </div>
          <FadeUp delay={0.1} className={`relative mt-12 aspect-[21/9] overflow-hidden ${CARD} lg:mt-16`}>
            <ORImage
              alt="Sécurité avec We Are Scene"
              className="object-cover"
              fill
              sizes="(max-width: 1240px) 100vw, 1176px"
              src={`https://officeriders-imager.s3.amazonaws.com/${getImageOutside(`insurance-2.${extension}`, ua)}`}
            />
          </FadeUp>
        </div>
      </section>

      {/* Ce qui est couvert */}
      <section className="border-t border-was-ink/8 bg-white">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="03">{kicker(langCurrent, 'insurance', 2)}</Kicker>
          </FadeUp>
          <div className="mt-10 grid gap-10 border-t border-was-ink/8 md:grid-cols-2 md:gap-16">
            {['small', 'big'].map((k, i) => (
              <FadeUp key={k} delay={i * 0.08} className="pt-10">
                <h2 className={`${H2} text-balance`}>{t(`insurance:${k}.title`)}</h2>
                <Markdown className={`mt-5 ${MD}`}>{t(`insurance:${k}.text`)}</Markdown>
                <Markdown className={`insurance-steps mt-8 ${MD}`}>{t(`insurance:${k}.list`)}</Markdown>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-was-ink/8 bg-was-cream">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="04">{kicker(langCurrent, 'insurance', 3)}</Kicker>
          </FadeUp>
          <div className="mt-10">
            <NumberedList items={faq} columns={3} />
          </div>
        </div>
      </section>

      {/* Aller plus loin */}
      <section className="border-t border-was-ink/8 bg-white">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="05">{kicker(langCurrent, 'insurance', 4)}</Kicker>
            <h2 className={`mt-5 max-w-[760px] text-balance ${H2}`}>{t('insurance:others')}</h2>
          </FadeUp>
          <Stagger stagger={0.06} delay={0.1} className="mt-10 grid gap-3 md:grid-cols-2">
            {HELP_LINKS.map(([k, href]) => (
              <StaggerChild key={k}>
                <a rel="nofollow" href={href} className={`group flex items-center justify-between gap-4 px-6 py-4 text-[15px] text-was-ink ${CARD} transition duration-200 hover:border-was-ink/25`}>
                  <span>{t(`insurance:faq.link.${k}`)}</span>
                  <span className="shrink-0 text-was-muted transition group-hover:translate-x-0.5 group-hover:text-was-ink"><ArrowIcon /></span>
                </a>
              </StaggerChild>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
});
